import { collection, query, where, getDocs, doc, getDoc, addDoc, Timestamp, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

export const observationService = {
  // 학생의 관찰 항목 가져오기
  getObservationBehaviors: async (studentId) => {
    try {
      const q = query(
        collection(db, 'observation_behaviors'),
        where('studentId', '==', studentId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const behaviors = [];
      querySnapshot.forEach((docSnapshot) => {
        behaviors.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });

      return behaviors;
    } catch (error) {
      console.error('Error fetching observation behaviors:', error);
      throw new Error('관찰 항목을 불러오는데 실패했습니다.');
    }
  },

  // 관찰 항목 추가
  addObservationBehavior: async (studentId, data) => {
    try {
      const behaviorData = {
        studentId,
        name: data.name,
        description: data.description || '',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: data.createdBy
      };

      const docRef = await addDoc(collection(db, 'observation_behaviors'), behaviorData);
      return { id: docRef.id, ...behaviorData };
    } catch (error) {
      console.error('Error adding observation behavior:', error);
      throw new Error('관찰 항목 추가에 실패했습니다.');
    }
  },

  // 관찰 항목 수정
  updateObservationBehavior: async (behaviorId, data) => {
    try {
      const behaviorRef = doc(db, 'observation_behaviors', behaviorId);
      const updateData = {
        name: data.name,
        description: data.description || '',
        updatedAt: Timestamp.now()
      };

      await updateDoc(behaviorRef, updateData);
      return { id: behaviorId, ...updateData };
    } catch (error) {
      console.error('Error updating observation behavior:', error);
      throw new Error('관찰 항목 수정에 실패했습니다.');
    }
  },

  // 관찰 항목 삭제
  deleteObservationBehavior: async (behaviorId) => {
    try {
      const behaviorRef = doc(db, 'observation_behaviors', behaviorId);
      await deleteDoc(behaviorRef);

      // 관련된 모든 기록도 삭제
      const recordsQuery = query(
        collection(db, 'observation_records'),
        where('behaviorId', '==', behaviorId)
      );
      const recordsSnapshot = await getDocs(recordsQuery);

      const deletePromises = [];
      recordsSnapshot.forEach((docSnapshot) => {
        deletePromises.push(deleteDoc(doc(db, 'observation_records', docSnapshot.id)));
      });

      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting observation behavior:', error);
      throw new Error('관찰 항목 삭제에 실패했습니다.');
    }
  },

  // 특정 행동의 관찰 기록 가져오기
  getObservationRecords: async (studentId, behaviorId, startDate = null, endDate = null) => {
    try {
      let q = query(
        collection(db, 'observation_records'),
        where('studentId', '==', studentId),
        where('behaviorId', '==', behaviorId)
      );

      if (startDate) {
        q = query(q, where('date', '>=', startDate));
      }

      if (endDate) {
        q = query(q, where('date', '<=', endDate));
      }

      q = query(q, orderBy('date', 'desc'));

      const querySnapshot = await getDocs(q);

      const records = [];
      querySnapshot.forEach((docSnapshot) => {
        records.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });

      return records;
    } catch (error) {
      console.error('Error fetching observation records:', error);
      throw new Error('관찰 기록을 불러오는데 실패했습니다.');
    }
  },

  // 학생의 전체 관찰 요약 (날짜별)
  getObservationSummary: async (studentId) => {
    try {
      // 모든 관찰 항목 가져오기
      const behaviors = await observationService.getObservationBehaviors(studentId);

      // 오늘, 이번 주, 이번 달 날짜 계산
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoStr = weekAgo.toISOString().split('T')[0];

      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      const monthAgoStr = monthAgo.toISOString().split('T')[0];

      // 각 항목별로 요약 정보 생성
      const summaries = await Promise.all(
        behaviors.map(async (behavior) => {
          // 모든 기록 가져오기
          const allRecords = await observationService.getObservationRecords(studentId, behavior.id);

          // 오늘 카운트
          const todayRecord = allRecords.find(r => r.date === todayStr);
          const todayCount = todayRecord ? todayRecord.count : 0;

          // 이번 주 카운트
          const weekRecords = allRecords.filter(r => r.date >= weekAgoStr);
          const weekCount = weekRecords.reduce((sum, r) => sum + (r.count || 0), 0);

          // 이번 달 카운트
          const monthRecords = allRecords.filter(r => r.date >= monthAgoStr);
          const monthCount = monthRecords.reduce((sum, r) => sum + (r.count || 0), 0);

          return {
            behaviorId: behavior.id,
            behaviorName: behavior.name,
            behaviorDescription: behavior.description,
            todayCount,
            weekCount,
            monthCount,
            allRecords: allRecords.slice(0, 30) // 최근 30개만
          };
        })
      );

      return summaries;
    } catch (error) {
      console.error('Error fetching observation summary:', error);
      throw new Error('관찰 요약 정보를 불러오는데 실패했습니다.');
    }
  },

  // 특정 날짜의 관찰 기록 가져오기
  getObservationRecordsForDate: async (studentId, date) => {
    try {
      const q = query(
        collection(db, 'observation_records'),
        where('studentId', '==', studentId),
        where('date', '==', date)
      );

      const querySnapshot = await getDocs(q);

      const records = [];
      querySnapshot.forEach((docSnapshot) => {
        records.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });

      return records;
    } catch (error) {
      console.error('Error fetching observation records for date:', error);
      throw new Error('해당 날짜의 관찰 기록을 불러오는데 실패했습니다.');
    }
  }
};
